import { H3 } from "components/Typography/text.styled";
import { ButtonStyled } from "components/buttons/buttons.styled";
import { SystemFormData } from "model/Company";
import { PaymentMethod } from "model/Gig";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Prompt } from "react-router-dom";
import { Api } from "services/api/api.service";
import { useAlertStore } from "stores/alertStore/alertStore";
import { normalizeNumber } from "web-apps/company/components/organizationalStructure/editModal.component";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { AutoGenerateShiftSetting } from "../../companyStampClock/autoGenerateShiftSetting.component";
import { DividerLine } from "../../companyStampClock/companyStampClock.styled";
import { LocalStampClockSetting } from "../../companyStampClock/localStampClockSetting.component";
import { AutoAcceptAllGigsSetting } from "./autoAcceptAllGigsSetting.component";
import { BreakSetting } from "./breakSetting.component";
import { CompanyTimeReportIncludeFlexTimes } from "./companyTimeReportIncludeFlexTimes.component";
import { CompanyMonthlySalaryIgnoreAbsenceSetting } from "./companyMonthlySalaryIgnoreAbsenceSetting.component";
import { CompanyTimeZoneSetting } from "./companyTimeZoneSetting.component";
import { DeductionSetting } from "./deductionSetting.component";
import { PercentageInput } from "./percentageInput.component";
import { TimeSettings } from "./timeSettings.component";
import { AllowEditTimeStampClockSetting } from "./allowEditTimeStampClockSetting.component";
import { TimeReportDeviationThresholdSetting } from "./timeReportDeviationThresholdSetting.component";
import { TimeReportStampTreshHoldSettings } from "./timeReportStampTresholdSettings.component";

type SettingKey =
  | "GigPayDeduction"
  | "GigBreakTime"
  | "CompanyTimeReportDeviationThreshold"
  | "CompanyTimeReportStampBeforeStartThreshold"
  | "CompanyTimeReportStampAfterStartThreshold"
  | "CompanyTimeReportStampBeforeEndThreshold"
  | "CompanyTimeReportStampAfterEndThreshold"
  | "CompanyTimeReportIncludeFlexTimes"
  | "CompanyWorkerAutoAcceptAllGigs"
  | "CompanyMonthlySalaryIgnoreAbsence"
  | "CompanyVacationPayPercentage"
  | "CompanyTimeZone"
  | "CompanyMonthlySalaryVacationPayPercent"
  | "LocalStampClock"
  | "StampClockAllowAutoGenerate"
  | "CompanyGigShiftStartTime"
  | "CompanyGigShiftEndTime"
  | "CompanyStampClockAllowEditTime";

const DEFAULT_TZ = "Europe/Stockholm";

const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n));

const buildISOFromHM = (h: number, m: number, s: number = 0): string => {
  const d = new Date();
  d.setHours(h, m, s, 0);
  return d.toISOString();
};

const DEFAULT_START_ISO = buildISOFromHM(8, 0, 0);
const DEFAULT_END_ISO = buildISOFromHM(17, 0, 0);
const NONE_ABSENCE_VALUE = "None";

const isoToHms = (value: string | undefined, fallback: string): string => {
  if (!value?.trim()) return fallback;
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return fallback;
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(
      d.getSeconds(),
    )}`;
  } catch {
    return fallback;
  }
};

const backendTimeToISO = (value: string | undefined, fallbackISO: string) => {
  const s = value?.trim();
  if (!s) return fallbackISO;

  if (s.includes("T")) return s;

  const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return fallbackISO;

  const h = Number(m[1]);
  const mi = Number(m[2]);
  const se = Number(m[3] ?? "0");

  let base = new Date(fallbackISO);
  if (Number.isNaN(base.getTime())) base = new Date();

  base.setHours(h, mi, se, 0);
  return base.toISOString();
};

const decimalToPercent = (
  decimal: number | null | undefined,
  fallbackPercent: number,
): number => {
  if (decimal === null || decimal === undefined) return fallbackPercent;
  return decimal * 100;
};

const percentToDecimalString = (
  percent: number | null | undefined,
  fallbackPercent: number,
): string => {
  const p = (percent ?? fallbackPercent) / 100;
  const rounded = Math.round(p * 1e6) / 1e6;
  return String(rounded);
};

const toStringArray = (v: string | undefined): string[] => {
  const s = v?.trim();
  if (!s) return [];

  try {
    const parsed = JSON.parse(s);
    if (Array.isArray(parsed)) return parsed.map((x) => String(x));
  } catch {}

  return s
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
};

const enforceNoneRules = (arr: string[]): string[] => {
  const unique = Array.from(new Set(arr.filter(Boolean).map(String)));

  if (unique.length === 0) return [NONE_ABSENCE_VALUE];

  const hasNonNone = unique.some((x) => x !== NONE_ABSENCE_VALUE);
  return hasNonNone
    ? unique.filter((x) => x !== NONE_ABSENCE_VALUE)
    : [NONE_ABSENCE_VALUE];
};

const arrayToStringForBackend = (arr: string[]): string =>
  enforceNoneRules(arr).join(", ");

const stringNormalize = (s: string): string =>
  arrayToStringForBackend(toStringArray(s));

export const toBool = (v: string | undefined, fallback = false): boolean => {
  const s = v?.trim().toLowerCase();
  if (!s) return fallback;
  return s === "true" || s === "1";
};

const toNumber = (v: string | undefined, fallback = 0): number =>
  normalizeNumber(v ?? "") ?? fallback;

const DEFAULT_FORM: SystemFormData = {
  preferedPaymentMethod: undefined,
  deduction: 0,
  break: 0,
  timeReportDeviationThreshold: 0,
  timeReportStampBeforeStartThreshold: 0,
  timeReportStampAfterStartThreshold: 0,
  timeReportStampBeforeEndThreshold: 0,
  timeReportStampAfterEndThreshold: 0,
  companyTimeReportIncludeFlexTimes: false,
  autoAcceptAllGigs: false,
  companyMonthlySalaryIgnoreAbsence: NONE_ABSENCE_VALUE,
  vacationPayPercentage: 12,
  companyTimeZone: DEFAULT_TZ,
  companyMonthlySalaryVacationPayPercent: 0.43,
  localStampClockSettingActive: false,
  autoGenerateShiftSettingActive: false,
  companyGigShiftStartTime: DEFAULT_START_ISO,
  companyGigShiftEndTime: DEFAULT_END_ISO,
  companyStampClockAllowEditTime: false,
};

export const CompanySystemSettings: React.FC = () => {
  const { t } = useTranslation();
  const [companyState, , companyDispatch] = useCompanyStore();
  const [, , alertDispatch] = useAlertStore();

  const [formData, setFormData] = useState<SystemFormData>(DEFAULT_FORM);
  const savedSettingsRef = useRef<SystemFormData>(DEFAULT_FORM);

  const haveStampClockModule = useMemo(() => {
    return (
      companyState.companySettings?.find(
        (s: any) => s.key === "CompanyShowStampclockModule",
      )?.value == "true"
    );
  }, [companyState.companySettings]);

  const showAlert = useCallback(
    (error?: boolean) => {
      alertDispatch({
        type: "SHOW_ALERT",
        payload: !error
          ? {
              icon: "checkmark",
              title: t("Alerts.Updated"),
              message: t("System.CompanySystemSettingsUpdated"),
            }
          : {
              icon: "alert",
              title: t("Alerts.OhNo"),
              message: t("System.CompanySystemSettingsNotUpdated"),
            },
      });
    },
    [alertDispatch, t],
  );

  useEffect(() => {
    if (!companyState.company?.id) return;

    let cancelled = false;

    Api()
      .company.getCompanySettings(companyState.company.id)
      .then((res) => {
        if (cancelled) return;

        const get = (k: string): string | undefined =>
          res.data.find((s: { key: string; value: string }) => s.key === k)
            ?.value;

        const ignoreAbsString = arrayToStringForBackend(
          enforceNoneRules(
            toStringArray(get("CompanyMonthlySalaryIgnoreAbsence")),
          ),
        );

        const init: SystemFormData = {
          preferedPaymentMethod: (get("DefaultGigPaymentType") || undefined) as
            | PaymentMethod
            | undefined,

          deduction: toNumber(get("GigPayDeduction"), 0),
          break: toNumber(get("GigBreakTime"), 0),
          timeReportDeviationThreshold: toNumber(
            get("CompanyTimeReportDeviationThreshold"),
            0,
          ),
          timeReportStampBeforeStartThreshold: toNumber(
            get("CompanyTimeReportStampBeforeStartThreshold"),
            0,
          ),
          timeReportStampAfterStartThreshold: toNumber(
            get("CompanyTimeReportStampAfterStartThreshold"),
            0,
          ),
          timeReportStampBeforeEndThreshold: toNumber(
            get("CompanyTimeReportStampBeforeEndThreshold"),
            0,
          ),
          timeReportStampAfterEndThreshold: toNumber(
            get("CompanyTimeReportStampAfterEndThreshold"),
            0,
          ),
          companyTimeReportIncludeFlexTimes: toBool(
            get("CompanyTimeReportIncludeFlexTimes"),
            false,
          ),

          autoAcceptAllGigs: toBool(
            get("CompanyWorkerAutoAcceptAllGigs"),
            false,
          ),

          companyMonthlySalaryIgnoreAbsence: ignoreAbsString,

          vacationPayPercentage: decimalToPercent(
            toNumber(get("CompanyVacationPayPercentage"), 0.12),
            12,
          ),

          companyTimeZone: get("CompanyTimeZone") ?? DEFAULT_TZ,

          companyMonthlySalaryVacationPayPercent: decimalToPercent(
            toNumber(get("CompanyMonthlySalaryVacationPayPercent"), 0.0043),
            0.43,
          ),

          localStampClockSettingActive: toBool(get("LocalStampClock"), false),
          autoGenerateShiftSettingActive: toBool(
            get("StampClockAllowAutoGenerate"),
            false,
          ),

          companyGigShiftStartTime: backendTimeToISO(
            get("CompanyGigShiftStartTime"),
            DEFAULT_START_ISO,
          ),
          companyGigShiftEndTime: backendTimeToISO(
            get("CompanyGigShiftEndTime"),
            DEFAULT_END_ISO,
          ),

          companyStampClockAllowEditTime: toBool(
            get("CompanyStampClockAllowEditTime"),
            false,
          ),
        };

        savedSettingsRef.current = init;
        setFormData(init);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [companyState.company?.id]);

  const diffPlan: {
    key: SettingKey;
    pick: (f: SystemFormData) => unknown;
    toBackendString: (v: unknown) => string;
  }[] = [
    {
      key: "GigPayDeduction",
      pick: (f) => f.deduction,
      toBackendString: (v) => String(Number(v ?? 0)),
    },
    {
      key: "GigBreakTime",
      pick: (f) => f.break,
      toBackendString: (v) => String(Number(v ?? 0)),
    },
    {
      key: "CompanyTimeReportDeviationThreshold",
      pick: (f) => f.timeReportDeviationThreshold,
      toBackendString: (v) => String(Number(v ?? 0)),
    },
    {
      key: "CompanyTimeReportStampBeforeStartThreshold",
      pick: (f) => f.timeReportStampBeforeStartThreshold,
      toBackendString: (v) => String(Number(v ?? 0)),
    },
    {
      key: "CompanyTimeReportStampAfterStartThreshold",
      pick: (f) => f.timeReportStampAfterStartThreshold,
      toBackendString: (v) => String(Number(v ?? 0)),
    },
    {
      key: "CompanyTimeReportStampBeforeEndThreshold",
      pick: (f) => f.timeReportStampBeforeEndThreshold,
      toBackendString: (v) => String(Number(v ?? 0)),
    },
    {
      key: "CompanyTimeReportStampAfterEndThreshold",
      pick: (f) => f.timeReportStampAfterEndThreshold,
      toBackendString: (v) => String(Number(v ?? 0)),
    },
    {
      key: "CompanyTimeReportIncludeFlexTimes",
      pick: (f) => f.companyTimeReportIncludeFlexTimes,
      toBackendString: (v) => String(Boolean(v)),
    },
    {
      key: "CompanyWorkerAutoAcceptAllGigs",
      pick: (f) => f.autoAcceptAllGigs,
      toBackendString: (v) => String(Boolean(v)),
    },
    {
      key: "CompanyMonthlySalaryIgnoreAbsence",
      pick: (f) => f.companyMonthlySalaryIgnoreAbsence,
      toBackendString: (v) => stringNormalize(String(v ?? "")),
    },
    {
      key: "CompanyVacationPayPercentage",
      pick: (f) => f.vacationPayPercentage,
      toBackendString: (v) => percentToDecimalString(Number(v ?? 12), 12),
    },
    {
      key: "CompanyTimeZone",
      pick: (f) => f.companyTimeZone,
      toBackendString: (v) => String(v ?? DEFAULT_TZ),
    },
    {
      key: "CompanyMonthlySalaryVacationPayPercent",
      pick: (f) => f.companyMonthlySalaryVacationPayPercent,
      toBackendString: (v) => percentToDecimalString(Number(v ?? 0.43), 0.43),
    },
    {
      key: "LocalStampClock",
      pick: (f) => f.localStampClockSettingActive,
      toBackendString: (v) => String(Boolean(v)),
    },
    {
      key: "StampClockAllowAutoGenerate",
      pick: (f) => f.autoGenerateShiftSettingActive,
      toBackendString: (v) => String(Boolean(v)),
    },
    {
      key: "CompanyGigShiftStartTime",
      pick: (f) => f.companyGigShiftStartTime,
      toBackendString: (v) => isoToHms(String(v ?? ""), "08:00:00"),
    },
    {
      key: "CompanyGigShiftEndTime",
      pick: (f) => f.companyGigShiftEndTime,
      toBackendString: (v) => isoToHms(String(v ?? ""), "17:00:00"),
    },
    {
      key: "CompanyStampClockAllowEditTime",
      pick: (f) => f.companyStampClockAllowEditTime,
      toBackendString: (v) => String(Boolean(v)),
    },
  ];

  const changedSettings = useMemo(() => {
    const current = formData;
    const baseline = savedSettingsRef.current;

    return diffPlan
      .map(({ key, pick, toBackendString }) => {
        const curS = toBackendString(pick(current));
        const baseS = toBackendString(pick(baseline));
        return curS !== baseS ? { key, value: curS } : null;
      })
      .filter(Boolean) as { key: SettingKey; value: string }[];
  }, [formData]);

  const madeChanges = changedSettings.length > 0;

  const handleUpdate = useCallback(() => {
    if (!companyState.company?.id || changedSettings.length === 0) return;

    Promise.all(
      changedSettings.map((s) =>
        Api().company.systemSettings.updateCompanySetting(
          companyState.company!.id,
          s.key,
          s.value,
        ),
      ),
    )
      .then(() => {
        changedSettings.forEach((s) => {
          companyDispatch({
            type: "UPDATE_COMPANY_SETTINGS",
            payload: { key: s.key, value: s.value },
          });
        });

        savedSettingsRef.current = { ...formData };
        setFormData((prev) => ({ ...prev }));
        showAlert();
      })
      .catch(() => {
        showAlert(true);
      });
  }, [
    companyState.company?.id,
    changedSettings,
    companyDispatch,
    formData,
    showAlert,
  ]);

  return (
    <>
      <Prompt
        when={madeChanges}
        message={t("ProfileCompany.YouHaveUnSavedChanges")}
      />

      <H3>{t("System.System")}</H3>

      <DividerLine />

      <DeductionSetting
        value={formData.deduction}
        onChange={(v) => setFormData((p) => ({ ...p, deduction: v }))}
      />

      <BreakSetting
        value={formData.break}
        onChange={(v) => setFormData((p) => ({ ...p, break: v }))}
      />

      <TimeReportDeviationThresholdSetting
        value={formData.timeReportDeviationThreshold}
        onChange={(v) =>
          setFormData((p) => ({ ...p, timeReportDeviationThreshold: v }))
        }
      />
      <CompanyTimeReportIncludeFlexTimes
        value={formData.companyTimeReportIncludeFlexTimes}
        onChange={(v) =>
          setFormData((p) => ({
            ...p,
            companyTimeReportIncludeFlexTimes: v,
          }))
        }
      />

      <TimeSettings
        startTime={formData.companyGigShiftStartTime}
        endTime={formData.companyGigShiftEndTime}
        onChangeStartTime={(v) =>
          setFormData((p) => ({ ...p, companyGigShiftStartTime: v }))
        }
        onChangeEndTime={(v) =>
          setFormData((p) => ({ ...p, companyGigShiftEndTime: v }))
        }
      />

      <AutoAcceptAllGigsSetting
        value={formData.autoAcceptAllGigs}
        onChange={(v) => setFormData((p) => ({ ...p, autoAcceptAllGigs: v }))}
      />

      <CompanyMonthlySalaryIgnoreAbsenceSetting
        value={formData.companyMonthlySalaryIgnoreAbsence}
        onChange={(string) =>
          setFormData((p) => ({
            ...p,
            companyMonthlySalaryIgnoreAbsence: stringNormalize(string),
          }))
        }
      />

      <PercentageInput
        value={formData.vacationPayPercentage}
        onChange={(v) =>
          setFormData((p) => ({ ...p, vacationPayPercentage: v }))
        }
        min={12}
        max={25}
        title={t("System.CompanyVacationPayPercentage")}
        info={t("System.CompanyVacationPayPercentageInfo")}
      />

      <PercentageInput
        value={formData.companyMonthlySalaryVacationPayPercent}
        onChange={(v) =>
          setFormData((p) => ({
            ...p,
            companyMonthlySalaryVacationPayPercent: v,
          }))
        }
        min={0.43}
        max={1}
        title={t("System.CompanyMonthlyVacationPayPercentage")}
        info={t("System.CompanyMonthlyVacationPayPercentageInfo")}
      />

      <AllowEditTimeStampClockSetting
        value={formData.companyStampClockAllowEditTime}
        onChange={(v) =>
          setFormData((p) => ({
            ...p,
            companyStampClockAllowEditTime: v,
          }))
        }
      />

      {haveStampClockModule && (
        <>
          <TimeReportStampTreshHoldSettings
            beforeStartValue={formData.timeReportStampBeforeStartThreshold}
            onChangeBeforeStart={(v) =>
              setFormData((p) => ({
                ...p,
                timeReportStampBeforeStartThreshold: v,
              }))
            }
            afterStartValue={formData.timeReportStampAfterStartThreshold}
            onChangeAfterStart={(v) =>
              setFormData((p) => ({
                ...p,
                timeReportStampAfterStartThreshold: v,
              }))
            }
            beforeEndValue={formData.timeReportStampBeforeEndThreshold}
            onChangeBeforeEnd={(v) =>
              setFormData((p) => ({
                ...p,
                timeReportStampBeforeEndThreshold: v,
              }))
            }
            afterEndValue={formData.timeReportStampAfterEndThreshold}
            onChangeAfterEnd={(v) =>
              setFormData((p) => ({
                ...p,
                timeReportStampAfterEndThreshold: v,
              }))
            }
          />

          <LocalStampClockSetting
            value={formData.localStampClockSettingActive}
            onChange={(v) =>
              setFormData((p) => ({
                ...p,
                localStampClockSettingActive: v,
              }))
            }
          />

          <AutoGenerateShiftSetting
            value={formData.autoGenerateShiftSettingActive}
            onChange={(v) =>
              setFormData((p) => ({
                ...p,
                autoGenerateShiftSettingActive: v,
              }))
            }
          />
        </>
      )}

      <CompanyTimeZoneSetting
        value={formData.companyTimeZone}
        onChange={(v) => setFormData((p) => ({ ...p, companyTimeZone: v }))}
      />

      <ButtonStyled disabled={!madeChanges} onClick={handleUpdate}>
        {t("General.Save")}
      </ButtonStyled>
    </>
  );
};
